import { routerNavigatedAction } from '@ngrx/router-store';
import { createReducer, on } from '@ngrx/store';
import { getEmbeddedResource, getUrl } from '@ngxp/rest';
import { createEmptyStateResource, createStateResource, StateResource } from '@schir-int-client/ngrx-helpers';
import { appendVorgaengeForRueckLauf, createListResource, replaceResourceInMap, sameUri } from '@schir-int-client/tech';
import {
	loadVerfahrenSingleSuccessAction,
	updateVerfahrenSuccessAction,
	VerfahrenResource,
} from '@schir-int-client/verfahren-shared';
import {
	createVorgangInVerfahrenSuccessAction,
	deleteVorgangSuccessAction,
	loadVorgaengeByVerfahrenSuccessAction,
	loadVorgaengeInRuecklaufSuccessAction,
	loadVorgaengeInWiedervorlageAction,
	loadVorgaengeInWiedervorlageSuccessAction,
	loadVorgaengeRechtspflegeSuccessAction,
	loadVorgangSuccessAction,
	selectVorgangAction,
	setVorgangStatusSuccessAction,
	updateDatumsBereichAction,
	updateVorgangNotizSuccessAction,
} from './vorgang.actions';
import { VorgangLinkRel, VorgangListLinkRel } from './vorgang.linkrel';
import { DatumsBereich, VorgangListResource, VorgangResource } from './vorgang.model';
import moment from 'moment';

export interface VorgangState {
	selectedVorgang: StateResource<VorgangResource>;
	vorgaengeByVerfahren: {
		[verfahrenUri: string]: VorgangResource[];
	};
	vorgaengeRechtspflege: VorgangResource[];
	vorgaengeInRuecklauf: VorgangListResource;
	vorgaengeInWiedervorlage: StateResource<VorgangListResource>;
	datumsBereich: DatumsBereich;
}

export const initialState: VorgangState = {
	selectedVorgang: createEmptyStateResource(),
	vorgaengeByVerfahren: {},
	vorgaengeRechtspflege: null,
	vorgaengeInRuecklauf: null,
	vorgaengeInWiedervorlage: createEmptyStateResource(),
	datumsBereich: {
		anfangsDatum: moment(0),
		endDatum: moment().add(14, 'days'),
	},
};

export const vorgangReducer = createReducer(
	initialState,
	on(selectVorgangAction, (state: VorgangState, { vorgang }): VorgangState => ({
		...state,
		selectedVorgang: createStateResource(vorgang),
	})),
	on(loadVorgangSuccessAction, (state: VorgangState, { vorgang }): VorgangState => ({
		...state,
		selectedVorgang: sameUri(vorgang, state.selectedVorgang.resource)
			? createStateResource(vorgang)
			: state.selectedVorgang,
		vorgaengeByVerfahren: replaceResourceInMap(state.vorgaengeByVerfahren, vorgang),
	})),
	on(createVorgangInVerfahrenSuccessAction, (state: VorgangState, { vorgaenge, verfahren }): VorgangState => {
		const embeddedResource: VorgangResource[] = getEmbeddedResource(vorgaenge, VorgangListLinkRel.VORGANG_LIST);
		return {
			...state,
			vorgaengeByVerfahren: {
				...state.vorgaengeByVerfahren,
				[getUrl(verfahren)]: embeddedResource,
			},
			selectedVorgang: createStateResource(embeddedResource[0]),
		};
	}),
	on(loadVorgaengeByVerfahrenSuccessAction, (state: VorgangState, { vorgaenge, verfahren }): VorgangState => {
		//set first vorgang from list as selected if none exists or a new verfahren was loaded
		if (!state.selectedVorgang.resource || verfahren.aktenzeichen !== state.selectedVorgang.resource.aktenzeichen) {
			if (vorgaenge._embedded && vorgaenge._embedded['vorgangList']) {
				const vorgangResource: VorgangResource = vorgaenge._embedded['vorgangList'][0];
				return {
					...state,
					selectedVorgang: createStateResource(vorgangResource, false),
					vorgaengeByVerfahren: {
						...state.vorgaengeByVerfahren,
						[getUrl(verfahren)]: getEmbeddedResource(vorgaenge, VorgangListLinkRel.VORGANG_LIST),
					},
				};
			}
		}
		return {
			...state,
			vorgaengeByVerfahren: {
				...state.vorgaengeByVerfahren,
				[getUrl(verfahren)]: getEmbeddedResource(vorgaenge, VorgangListLinkRel.VORGANG_LIST),
			},
		};
	}),
	on(loadVorgaengeRechtspflegeSuccessAction, (state: VorgangState, { vorgaenge }): VorgangState => ({
		...state,
		vorgaengeRechtspflege: vorgaenge,
	})),
	on(loadVorgaengeInRuecklaufSuccessAction, (state: VorgangState, { vorgaenge }): VorgangState => ({
		...state,
		vorgaengeInRuecklauf: vorgaenge,
	})),
	on(loadVerfahrenSingleSuccessAction, (state: VorgangState, { verfahren }): VorgangState => {
		//dont remove selection if the same verfahren was loaded
		if (
			state.selectedVorgang &&
			state.selectedVorgang.resource &&
			verfahren.aktenzeichen === state.selectedVorgang.resource.aktenzeichen
		) {
			return state;
		}
		return {
			...state,
			selectedVorgang: createEmptyStateResource(),
		};
	}),
	on(updateVorgangNotizSuccessAction, (state: VorgangState, { vorgang }): VorgangState => ({
		...state,
		vorgaengeByVerfahren: <{ [verfahrenUri: string]: VorgangResource[] }>(
			replaceResourceInMap(state.vorgaengeByVerfahren, vorgang)
		),
		selectedVorgang: getCurrentVorgang(state, vorgang),
	})),
	on(setVorgangStatusSuccessAction, (state: VorgangState, { vorgang }): VorgangState => ({
		//TODO SCHIFF-5196
		...state,
		vorgaengeByVerfahren: <{ [verfahrenUri: string]: VorgangResource[] }>(
			replaceResourceInMap(state.vorgaengeByVerfahren, vorgang)
		),
		selectedVorgang: getCurrentVorgang(state, vorgang),
		vorgaengeInRuecklauf: appendVorgaengeForRueckLauf(state, vorgang),
		vorgaengeInWiedervorlage: { ...state.vorgaengeInWiedervorlage, reload: true },
	})),
	on(routerNavigatedAction, (state: VorgangState, { payload }): VorgangState => {
		return payload.event.url.startsWith('/verfahren')
			? state
			: {
					...state,
				};
	}),
	on(deleteVorgangSuccessAction, (state: VorgangState, { uri }): VorgangState => {
		if (getUrl(state.selectedVorgang.resource) === uri) {
			return {
				...state,
				selectedVorgang: createEmptyStateResource(),
			};
		}
		return state;
	}),
	on(loadVorgaengeInWiedervorlageAction, (state: VorgangState): VorgangState => ({
		...state,
		vorgaengeInWiedervorlage: createEmptyStateResource(true),
	})),
	on(loadVorgaengeInWiedervorlageSuccessAction, (state: VorgangState, { wiedervorlagen }): VorgangState => ({
		...state,
		vorgaengeInWiedervorlage: createStateResource(wiedervorlagen),
	})),
	on(updateDatumsBereichAction, (state: VorgangState, { von, bis }): VorgangState => ({
		...state,
		datumsBereich: {
			anfangsDatum: von,
			endDatum: bis,
		},
	})),
	on(updateVerfahrenSuccessAction, (state: VorgangState, { verfahren }): VorgangState => {
		return {
			...state,
			...updateVorgaenge(state, verfahren),
		};
	})
);

function updateVorgaenge(
	state: VorgangState,
	v: VerfahrenResource
): {
	//TODO SCHIFF-5196
	vorgaengeInRuecklauf: VorgangListResource;
	vorgaengeRechtspflege: VorgangResource[];
	vorgaengeInWiedervorlage: StateResource<VorgangListResource>;
} {
	const vorgaengeInRuecklauf = state.vorgaengeInRuecklauf
		? createListResource(
				state.vorgaengeInRuecklauf,
				updateStandortAkte(v, getEmbeddedResource(state.vorgaengeInRuecklauf, VorgangListLinkRel.VORGANG_LIST)),
				VorgangListLinkRel.VORGANG_LIST
			)
		: state.vorgaengeInRuecklauf;

	const vorgaengeRechtspflege = state.vorgaengeRechtspflege
		? updateStandortAkte(v, state.vorgaengeRechtspflege)
		: state.vorgaengeRechtspflege;

	const vorgaengeInWiedervorlage =
		state.vorgaengeInWiedervorlage && state.vorgaengeInWiedervorlage.resource
			? createStateResource(
					createListResource(
						state.vorgaengeInWiedervorlage.resource,
						updateStandortAkte(
							v,
							getEmbeddedResource(
								state.vorgaengeInWiedervorlage.resource,
								VorgangListLinkRel.VORGANG_LIST
							)
						),
						VorgangListLinkRel.VORGANG_LIST
					)
				)
			: state.vorgaengeInWiedervorlage;

	return {
		vorgaengeInRuecklauf,
		vorgaengeRechtspflege,
		vorgaengeInWiedervorlage,
	};
}

function updateStandortAkte(v: VerfahrenResource, vorgaenge: VorgangResource[]): VorgangResource[] {
	const standortAkteKey = 'lfpr.standort_akte';

	return vorgaenge
		? vorgaenge.map(vorgang => {
				if (getUrl(vorgang, VorgangLinkRel.VERFAHREN) === getUrl(v)) {
					const verfahrenProperty = v.properties.find(value => value.key == standortAkteKey);
					return {
						...vorgang,
						standortAkte: verfahrenProperty?.value ?? '',
					};
				}
				return vorgang;
			})
		: vorgaenge;
}

function getCurrentVorgang(state: VorgangState, vorgang: VorgangResource): StateResource<VorgangResource> {
	return state.selectedVorgang.loaded ? createStateResource(vorgang) : state.selectedVorgang;
}
